import { useI18n } from 'vue-i18n'
import type { SessionUserInfo, User, SessionUserAttributes } from '@/types/User'
import { usePiniaStateFromLocalStorage } from '@/composables/usePiniaStateFromLocalStorage'
import { fetchCoreExternal } from '@/utils/request'
import { computed, ref, watch } from 'vue'
import usePendo from '@/composables/usePendo'

export const useUser = () => {
  const i18n = useI18n()
  const { initPendo } = usePendo()
  const storedState = usePiniaStateFromLocalStorage()

  const data = ref<User | null>(storedState?.auth?.user ?? null)
  const isTeacher = ref<boolean | null>(null)
  const isStudent = ref<boolean | null>(null)

  const fetchSessionUser = async (): Promise<SessionUserInfo> => {
    return await fetchCoreExternal('/frontend/session/user')
  }

  const fetchSessionAttributes = async (): Promise<SessionUserAttributes> => {
    return await fetchCoreExternal('/frontend/session/attributes')
  }

  async function init(): Promise<void> {
    try {
      const userData = await fetchSessionUser()
      const { gaHash } = await fetchSessionAttributes()
      data.value = { ...userData, gaHash }
    } catch (error) {
      throw new Error('Couldn\'t get user')
    }
  }

  function clear() {
    data.value = null
    isStudent.value = null
    isTeacher.value = null
  }

  function setRole(user: User) {
    isStudent.value = user.role.includes('student')
    isTeacher.value = user.role.includes('teacher')
  }

  const isInAschehougOrganization = computed(() => (
    data.value?.schoolOrganizations.some((org) => org.name.toLowerCase().includes('aschehoug'))
  ))

  const setLanguage = (user: User) => {
    i18n.locale.value = user.preferredLanguage ?? 'nb'
    document.documentElement.setAttribute('lang', i18n.locale.value)
  }

  watch<User | null>(data, (updated) => {
    if (!updated) return
    setRole(updated)
    initPendo(updated)
    setLanguage(updated)
  })

  return {
    data,
    init,
    isTeacher,
    isStudent,
    clear,
    setLanguage,
    isInAschehougOrganization,
  }
}
